import type { CSSProperties, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useResizeObserver } from '../../../../hooks/useResizeObserver';

interface PageDimensions {
    width: number;
    height: number;
}

interface PreviewPageScaleState {
    containerRef: RefObject<HTMLDivElement>;
    pageRef: RefObject<HTMLElement>;
    wrapperStyle?: CSSProperties;
    transformStyle?: CSSProperties;
}

// Keeps the page rendered at its natural dimensions and scales it down with CSS transform
// when the container is narrower than the page width.
export function usePreviewPageScale(): PreviewPageScaleState {
    const containerRef = useRef<HTMLDivElement>(null);
    const pageRef = useRef<HTMLElement>(null);
    const [pageDimensions, setPageDimensions] = useState<PageDimensions>({
        width: 0,
        height: 0,
    });
    const [pageScale, setPageScale] = useState(1);

    const updatePageDimensions = useCallback(() => {
        if (!pageRef.current) {
            return;
        }

        const width = pageRef.current.offsetWidth;
        const height = pageRef.current.offsetHeight;

        if (!width || !height) {
            return;
        }

        setPageDimensions((prev) =>
            prev.width === width && prev.height === height ? prev : { width, height }
        );
    }, []);

    const updatePageScale = useCallback(() => {
        if (!containerRef.current || !pageDimensions.width) {
            return;
        }

        const availableWidth = containerRef.current.clientWidth;
        if (!availableWidth) {
            return;
        }

        const nextScale = Math.min(1, availableWidth / pageDimensions.width);
        setPageScale((prev) => (Math.abs(prev - nextScale) < 0.001 ? prev : nextScale));
    }, [pageDimensions.width]);

    useResizeObserver(containerRef as RefObject<Element>, updatePageScale);
    useResizeObserver(pageRef as RefObject<Element>, updatePageDimensions);

    useEffect(() => {
        updatePageScale();
    }, [pageDimensions.width, updatePageScale]);

    const hasPageDimensions = pageDimensions.width > 0 && pageDimensions.height > 0;

    const wrapperStyle = hasPageDimensions
        ? {
              width: pageDimensions.width * pageScale,
              height: pageDimensions.height * pageScale,
          }
        : undefined;

    const transformStyle = hasPageDimensions
        ? {
              transform: `scale(${pageScale})`,
              transformOrigin: 'top center',
          }
        : undefined;

    return {
        containerRef,
        pageRef,
        wrapperStyle,
        transformStyle,
    };
}
